/**
 * The consent vocabulary, shared by the banner UI, the cookie layer and the tracking layer.
 *
 * <p>Nothing in here grants anything. An absent, unreadable, expired or out-of-date record means "no
 * consent" — never "assume yes". Statistics and marketing are independent choices and both start off,
 * so the only way a category becomes true is a visitor action that produced a <c>ConsentRecord</c>.</p>
 */

/**
 * Every category the visitor is asked about. <c>necessary</c> covers what the app cannot run without
 * (the session context, the language choice, and the consent cookie itself) and is therefore never
 * refusable — it is not a choice, so it is never presented as one.
 */
export type ConsentCategory = "necessary" | "statistics" | "marketing";

/** The categories a decision can actually switch on or off. */
export type OptionalConsentCategory = Exclude<ConsentCategory, "necessary">;

/**
 * The grant set. <c>necessary</c> is typed as the literal <c>true</c> rather than <c>boolean</c> so no
 * code path can construct a state where it is off, and so a caller reading it cannot mistake it for a
 * toggle.
 */
export interface ConsentCategories {
  readonly necessary: true;
  statistics: boolean;
  marketing: boolean;
}

/** What a banner or settings panel hands to <c>accept</c>: the two refusable categories, nothing else. */
export type ConsentChoice = Record<OptionalConsentCategory, boolean>;

/**
 * Which surface the decision was made on. Kept in the record because withdrawing has to be as easy as
 * granting, and a supervisory authority asking "how was this collected" needs an answer per receipt.
 */
export type ConsentSource = "banner" | "settings";

/**
 * The floor of the consent text version this build ships.
 *
 * <p>The version a visitor is actually asked against is the tenant's own — set in the admin tab, raised
 * whenever the tenant's vendor list changes, and delivered with the tracking configuration — and it can
 * only be higher than this. Bump this constant when the wording every tenant shares changes: the
 * categories, the dialog itself. Either way a stored record on an older version is discarded, so
 * everyone is asked again rather than silently carrying forward a grant they never gave.</p>
 */
export const CONSENT_POLICY_VERSION = 1;

/**
 * A stored decision — the consent receipt. It has to be enough, on its own, to show what was agreed,
 * when, on which site and brand, from which surface, and against which version of the text.
 */
export interface ConsentRecord {
  policyVersion: number;
  /** ISO 8601, UTC. */
  decidedAt: string;
  categories: ConsentCategories;
  /** Tenant the decision was made under — white-label means one browser can meet several brands. */
  tenant: string;
  /** Host the decision was made on. The cookie is host-only, so this pins the scope of the grant. */
  hostname: string;
  source: ConsentSource;
  /** Opaque id for this receipt. Random — never derived from anything that identifies the visitor. */
  consentReceiptId: string;
}

/** The reactive consent state held by the store. */
export interface ConsentState {
  /** The valid stored decision, or null when there is none for the current policy version. */
  record: ConsentRecord | null;
  categories: ConsentCategories;
  /** The settings panel is open. Shared here because the banner, the footer link and the panel itself
   *  all open it, and only one of them can own the flag. */
  settingsOpen: boolean;
  /** False once a write was refused (cookies blocked): the decision is real but holds for this tab
   *  only, and the UI can say so rather than pretending it was remembered. */
  persisted: boolean;
}

/**
 * What a subscriber is handed. The tracking layer needs <c>decided</c> as well as the categories:
 * "not answered yet" and "answered, necessary only" both mean no statistics, but only the second is a
 * settled state, and neither may inject anything.
 */
export interface ConsentSnapshot {
  decided: boolean;
  categories: ConsentCategories;
  record: ConsentRecord | null;
}

export type ConsentListener = (snapshot: ConsentSnapshot) => void;

/** The starting grant set: everything optional off. Always spread, never mutated. */
export const DEFAULT_CONSENT: ConsentCategories = {
  necessary: true,
  statistics: false,
  marketing: false,
};

/** Build a full grant set from the two refusable categories. Coerces, so a malformed caller widening
 *  a category by accident (undefined, "true") lands on false rather than on a grant. */
export function categoriesFrom(choice: ConsentChoice): ConsentCategories {
  return {
    necessary: true,
    statistics: choice.statistics === true,
    marketing: choice.marketing === true,
  };
}
